// AdminOffensesPage.jsx - Gestion des infractions et sanctions
import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription 
} from '../components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Gavel, 
  Plus, 
  Pencil, 
  Trash2, 
  Search,
  DollarSign,
  Clock,
  AlertTriangle
} from 'lucide-react';
import api from '../lib/api';

const OFFENSE_CATEGORIES = [
  { code: 'CONTRAVENTION', name: 'Contravention', color: 'bg-yellow-500' },
  { code: 'DELIT', name: 'Délit', color: 'bg-orange-500' },
  { code: 'CRIME', name: 'Crime', color: 'bg-red-500' }
];

const AdminOffensesPage = () => {
  const [offenses, setOffenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingOffense, setEditingOffense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: 'DELIT',
    min_fine: 0,
    max_fine: 0,
    min_imprisonment_days: 0,
    max_imprisonment_days: 0,
    additional_sanctions: []
  });

  const [newSanction, setNewSanction] = useState('');

  const loadOffenses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/offenses');
      setOffenses(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des infractions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOffenses();
  }, [loadOffenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOffense) {
        await api.put(`/offenses/${editingOffense.offense_id}`, formData);
        toast.success('Infraction mise à jour');
      } else {
        await api.post('/offenses', formData);
        toast.success('Infraction créée');
      }
      setShowDialog(false);
      resetForm();
      loadOffenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (offenseId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette infraction?')) return;
    
    try {
      await api.delete(`/offenses/${offenseId}`);
      toast.success('Infraction supprimée');
      loadOffenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleEdit = (offense) => {
    setEditingOffense(offense);
    setFormData({
      code: offense.code,
      name: offense.name,
      description: offense.description || '',
      category: offense.category,
      min_fine: offense.min_fine || 0,
      max_fine: offense.max_fine || 0,
      min_imprisonment_days: offense.min_imprisonment_days || 0,
      max_imprisonment_days: offense.max_imprisonment_days || 0,
      additional_sanctions: offense.additional_sanctions || []
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingOffense(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      category: 'DELIT',
      min_fine: 0,
      max_fine: 0,
      min_imprisonment_days: 0,
      max_imprisonment_days: 0,
      additional_sanctions: []
    });
  };

  const addSanction = () => {
    if (newSanction.trim()) {
      setFormData({
        ...formData,
        additional_sanctions: [...formData.additional_sanctions, newSanction.trim()]
      });
      setNewSanction('');
    }
  };

  const removeSanction = (index) => {
    setFormData({
      ...formData,
      additional_sanctions: formData.additional_sanctions.filter((_, i) => i !== index)
    });
  };

  const formatDays = (days) => {
    if (days === 0) return '-';
    if (days < 30) return `${days} jour(s)`;
    if (days < 365) return `${Math.floor(days / 30)} mois`;
    return `${Math.floor(days / 365)} an(s)`;
  };

  const filteredOffenses = offenses.filter(o => {
    const matchesSearch = o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         o.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (o.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || o.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryBadge = (category) => {
    const cat = OFFENSE_CATEGORIES.find(c => c.code === category);
    return cat ? (
      <Badge className={`${cat.color} text-white`}>{cat.name}</Badge>
    ) : (
      <Badge variant="secondary">{category}</Badge>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-offenses-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Gavel className="w-6 h-6" />
              Infractions & Sanctions
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérer le répertoire des infractions et leurs sanctions associées
            </p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-offense-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Infraction
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {OFFENSE_CATEGORIES.map(cat => (
            <Card key={cat.code}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{cat.name}s</p>
                    <p className="text-2xl font-bold">
                      {offenses.filter(o => o.category === cat.code).length}
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${cat.color} rounded-full flex items-center justify-center`}>
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher une infraction..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-offenses"
                />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger data-testid="filter-category">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  {OFFENSE_CATEGORIES.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Offenses List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOffenses.map((offense) => (
              <Card key={offense.offense_id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                          {offense.code}
                        </span>
                        {getCategoryBadge(offense.category)}
                      </div>
                      <h3 className="font-semibold text-lg">{offense.name}</h3>
                      {offense.description && (
                        <p className="text-muted-foreground text-sm mt-1">{offense.description}</p>
                      )}
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <div>
                            <p className="text-xs text-muted-foreground">Amende min</p>
                            <p className="font-medium">${offense.min_fine?.toLocaleString() || 0}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <div>
                            <p className="text-xs text-muted-foreground">Amende max</p>
                            <p className="font-medium">${offense.max_fine?.toLocaleString() || 0}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <div>
                            <p className="text-xs text-muted-foreground">Prison min</p>
                            <p className="font-medium">{formatDays(offense.min_imprisonment_days)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <div>
                            <p className="text-xs text-muted-foreground">Prison max</p>
                            <p className="font-medium">{formatDays(offense.max_imprisonment_days)}</p>
                          </div>
                        </div>
                      </div>

                      {offense.additional_sanctions?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {offense.additional_sanctions.map((s, i) => (
                            <Badge key={i} variant="outline">{s}</Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(offense)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(offense.offense_id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredOffenses.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                Aucune infraction trouvée
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingOffense ? 'Modifier l&apos;infraction' : 'Nouvelle infraction'}
              </DialogTitle>
              <DialogDescription>
                Définissez l&apos;infraction et les sanctions associées
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                <div>
                  <Label htmlFor="code">Code de l&apos;infraction *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="Ex: ART-123"
                    required
                    data-testid="offense-code-input"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Catégorie *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(v) => setFormData({...formData, category: v})}
                  >
                    <SelectTrigger data-testid="offense-category-select">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {OFFENSE_CATEGORIES.map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="name">Nom de l&apos;infraction *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ex: Vol à main armée"
                    required
                    data-testid="offense-name-input"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Description détaillée de l&apos;infraction..."
                    rows={3}
                  />
                </div>

                <div className="md:col-span-2">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Amendes (USD)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="min_fine">Amende minimum</Label>
                      <Input
                        id="min_fine"
                        type="number"
                        min="0"
                        value={formData.min_fine}
                        onChange={(e) => setFormData({...formData, min_fine: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="max_fine">Amende maximum</Label>
                      <Input
                        id="max_fine"
                        type="number"
                        min="0"
                        value={formData.max_fine}
                        onChange={(e) => setFormData({...formData, max_fine: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Peine d&apos;emprisonnement (en jours)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="min_imprisonment">Durée minimum</Label>
                      <Input
                        id="min_imprisonment"
                        type="number"
                        min="0"
                        value={formData.min_imprisonment_days}
                        onChange={(e) => setFormData({...formData, min_imprisonment_days: parseInt(e.target.value) || 0})}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDays(formData.min_imprisonment_days)}
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="max_imprisonment">Durée maximum</Label>
                      <Input
                        id="max_imprisonment"
                        type="number"
                        min="0"
                        value={formData.max_imprisonment_days}
                        onChange={(e) => setFormData({...formData, max_imprisonment_days: parseInt(e.target.value) || 0})}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDays(formData.max_imprisonment_days)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Label>Sanctions additionnelles</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newSanction}
                      onChange={(e) => setNewSanction(e.target.value)}
                      placeholder="Ex: Confiscation du véhicule"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSanction())}
                    />
                    <Button type="button" variant="outline" onClick={addSanction}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {formData.additional_sanctions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.additional_sanctions.map((s, i) => (
                        <Badge key={i} variant="secondary" className="flex items-center gap-1">
                          {s}
                          <button 
                            type="button"
                            onClick={() => removeSanction(i)}
                            className="ml-1 hover:text-destructive"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" data-testid="save-offense-btn">
                  {editingOffense ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminOffensesPage;
